// Resolves the kiosk's per-device runtime configuration.
//
// Two sources, in priority order:
//   1. window.AndroidBridge.config() — the production path inside the
//      kiosk-android WebView. Synchronous, so SPA boot stays synchronous.
//   2. Vite env vars — the desktop dev path.
//
// A malformed or throwing native config must never take the kiosk down: we
// fall back to env and resolve hasToken from whatever the env branch yields.
// On a deployed totem the env vars are unset by design (the SPA is public and
// must never ship a token), so an unprovisioned device lands on
// hasToken=false and App.tsx shows the out-of-service screen up front instead
// of 401-ing a visitor through the whole flow. In desktop dev the env vars
// are set, so the same fallback returns hasToken=true and dev keeps working.

import { kioskRuntimeConfigSchema } from "@entradart/kiosk-shared/bridge";

export type AndroidConfigApi = { config(): string };

export type ViteEnvLike = {
  VITE_BACKEND_BASE_URL?: string;
  VITE_KIOSK_TERMINAL_TOKEN?: string;
};

// Declared structurally rather than as `KioskRuntimeConfig & {...}`: the env
// branch can return backendBaseUrl: "", which kioskRuntimeConfigSchema
// (z.string().url()) rejects. Intersecting the schema type would claim every
// resolved config had passed validation when only the native branch has.
export type ResolvedRuntimeConfig = {
  /** Validated URL on the native path; possibly "" on the env path. */
  backendBaseUrl: string;
  /** Possibly "" — check hasToken before treating it as usable. */
  terminalToken: string;
  /** False when no usable token was found from either source. */
  hasToken: boolean;
  /** Which source won — surfaced in diagnostics and useful in bug reports. */
  source: "native" | "env";
};

const FALLBACK_WARNING = "[kiosk] native config unusable, falling back to env";

/**
 * Reduce a config failure to something safe to print.
 *
 * Deliberately never includes the payload: a JSON.parse SyntaxError echoes a
 * prefix of its input ("Unexpected token 'x', \"{...\" is not valid JSON"),
 * and that input is the config object carrying the terminal token. Zod issues
 * are reduced to path + code for the same reason. This is the one code path
 * that cannot be debugged on a deployed totem, so it must say something —
 * just never the secret.
 */
function describeConfigFailure(err: unknown): string {
  if (err instanceof SyntaxError) {
    return "SyntaxError: native config was not valid JSON";
  }
  if (err instanceof Error) {
    return `${err.name}: ${err.message}`;
  }
  return "unknown error";
}

export function resolveRuntimeConfig(
  env: ViteEnvLike,
  bridge: AndroidConfigApi | undefined
): ResolvedRuntimeConfig {
  if (bridge) {
    try {
      const parsed = kioskRuntimeConfigSchema.safeParse(
        JSON.parse(bridge.config())
      );
      if (parsed.success) {
        return { ...parsed.data, hasToken: true, source: "native" };
      }
      // Fall through to env. A broken bridge is a config problem, not a
      // reason to crash the visitor-facing app.
      console.warn(
        FALLBACK_WARNING,
        // Path + code only: an issue's `received` can echo the token back.
        parsed.error.issues.map(
          (issue) => `${issue.path.join(".") || "(root)"}: ${issue.code}`
        )
      );
    } catch (err) {
      console.warn(FALLBACK_WARNING, describeConfigFailure(err));
    }
  }

  const token = env.VITE_KIOSK_TERMINAL_TOKEN ?? "";
  return {
    backendBaseUrl: env.VITE_BACKEND_BASE_URL ?? "",
    terminalToken: token,
    hasToken: token.length > 0,
    source: "env",
  };
}
